import FactoryOutlined from "@mui/icons-material/FactoryOutlined";
import { Grid } from "@mui/system";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { ValueWithStaffEditedNotation } from "components/ValueWithStaffEditedNotation";
import { formatAddress } from "encamp-shared/src/utils/address";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteManifestSource } from "generated-graphql/graphql";

import { FieldSourceValues } from "../useManifestDiscrepancyFields";
import { ManifestPartyCard } from "./ManifestPartyCard";
import { SourcedField } from "./SourcedField";
import { GeneratorFacilityManifest } from "./types";

/**
 * Discrepancy rows carry raw ISO date strings; render them through the same
 * UTC date formatter the structural values use so the Ship Date stays in our
 * standard M/D/YYYY format regardless of which source the value resolves to.
 */
function formatShipDateValues(
  values: FieldSourceValues | undefined
): FieldSourceValues | undefined {
  if (!values) return undefined;
  const formatted: FieldSourceValues = {};
  for (const wasteSource of Object.values(WasteManifestSource)) {
    const value = values[wasteSource];
    if (value != null) formatted[wasteSource] = getUtcDisplayDate(value);
  }
  return formatted;
}

interface GeneratorFacilitySectionProps {
  manifest: GeneratorFacilityManifest;
  /** The selected data-source tab whose values are shown. */
  source: WasteManifestSource;
  availableSources: readonly WasteManifestSource[];
  valuesByField: Map<string, FieldSourceValues>;
  /** UI field keys with an open cross-source discrepancy — drives warning icons. */
  disagreementFields: Set<string>;
  /** Opens the full compare dialog from a field's provenance popover (staff only). */
  onOpenCompare?: () => void;
  /**
   * True when the card sits in the 3-up Generator → Transporters → Receiving
   * row: fields stack in a single column so they fit the ~1/3-width card.
   */
  narrow?: boolean;
}

/**
 * Generator card: the encamp-linked facility (name/address don't vary by
 * source) plus the source-split generator EPA ID, ship date, and vendor.
 */
export function GeneratorFacilitySection({
  manifest,
  source,
  availableSources,
  valuesByField,
  disagreementFields,
  onOpenCompare,
  narrow = false,
}: GeneratorFacilitySectionProps) {
  // Half-width at sm+, except in the narrow 3-up row where md+ is the squeezed
  // side-by-side layout (below md the cards stack full-width again).
  const fieldSize = { xs: 12, sm: 6, md: narrow ? 12 : 6 };
  const shippedDateEdited =
    !!manifest.vendorManifest &&
    manifest.vendorManifest.originalShippedDate != null &&
    manifest.vendorManifest.shippedDate !== manifest.vendorManifest.originalShippedDate;

  const vendorEdited =
    !!manifest.vendorManifest &&
    manifest.vendorManifest.originalVendorSource != null &&
    manifest.vendorManifest.vendorSource !== manifest.vendorManifest.originalVendorSource;

  return (
    <ManifestPartyCard title="Generator" icon={<FactoryOutlined fontSize="small" />}>
      <Grid container spacing={2}>
        <Grid size={fieldSize}>
          <DisplayOnlyField
            label="Facility Name"
            text={manifest.facility?.name ?? undefined}
            copyable={false}
          />
        </Grid>
        <Grid size={fieldSize}>
          <SourcedField
            label="Generator EPA ID"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.eManifest?.generatorId,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.generatorId,
              [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.generatorId,
            }}
            discrepancyValues={valuesByField.get("generatorId")}
            hasDisagreement={disagreementFields.has("generatorId")}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        <Grid size={12}>
          <DisplayOnlyField
            label="Facility Address"
            copyable={false}
            wrapValue
            text={formatAddress(
              manifest.facility?.streetAddress1,
              manifest.facility?.city,
              manifest.facility?.state,
              manifest.facility?.zip
            )}
          />
        </Grid>
        <Grid size={fieldSize}>
          <SourcedField
            label="Ship Date"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.eManifest?.shippedDate
                ? getUtcDisplayDate(manifest.eManifest.shippedDate)
                : undefined,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.shippedDate
                ? getUtcDisplayDate(manifest.vendorManifest.shippedDate)
                : undefined,
              [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.shippedDate
                ? getUtcDisplayDate(manifest.documentManifest.shippedDate)
                : undefined,
            }}
            discrepancyValues={formatShipDateValues(valuesByField.get("shippedDate"))}
            hasDisagreement={disagreementFields.has("shippedDate")}
            onOpenCompare={onOpenCompare}
            renderValue={
              source === WasteManifestSource.Vendor
                ? (value) => (
                    <ValueWithStaffEditedNotation value={value} isEdited={shippedDateEdited} />
                  )
                : undefined
            }
          />
        </Grid>
        {manifest.vendorManifest && (
          <Grid size={fieldSize}>
            <SourcedField
              label="Vendor"
              source={source}
              availableSources={availableSources}
              values={{ [WasteManifestSource.Vendor]: manifest.vendorManifest.vendorSource }}
              onOpenCompare={onOpenCompare}
              renderValue={
                source === WasteManifestSource.Vendor
                  ? (value) => (
                      <ValueWithStaffEditedNotation value={value} isEdited={vendorEdited} />
                    )
                  : undefined
              }
            />
          </Grid>
        )}
      </Grid>
    </ManifestPartyCard>
  );
}
